import * as ko from 'knockout';

import { app } from '../app';
import {
  CommitImportResult,
  ImportDimensionsDuplicatesAction,
  ManualResolutionAction,
  RowConflict,
  RowResolution,
  ValidateImportResult,
} from '../api/dimensions';
import i18n from '../i18n';
import { Deferred } from '../utils/deferred';
import { ManualResolutionOutcome } from './import_entities';

const template = require('raw-loader!../../templates/components/resolve_import_conflicts.html').default;

interface ConflictRow {
  conflict: RowConflict;
  existingRecord: string;
  selected: ko.Observable<boolean>;
  action: ko.Observable<ManualResolutionAction>;
  checkboxId: string;
}

interface Params {
  conflicts: RowConflict[];
  newCount: number;
  result: Deferred<RowResolution[]>;
}

class ResolveImportConflicts {
  title: string;
  summary: string;
  newCount: number;
  rows: ConflictRow[];
  showSheet: boolean;
  bulkAction = ko.observable<ManualResolutionAction>('ignore');
  actionOptions: { id: ManualResolutionAction; name: string }[] = [
    { id: 'ignore', name: i18n.t('Ignore (keep existing)')() },
    { id: 'update', name: i18n.t('Update existing')() },
    { id: 'add', name: i18n.t('Add as new')() },
  ];
  allSelected: ko.Computed<boolean>;
  selectAllId: string;

  private result: Deferred<RowResolution[]>;

  constructor(params: Params) {
    // A per-instance prefix keeps the checkbox ids unique across rows (and across dialogs).
    const idPrefix = 'resolve-conflict-' + Math.floor(Math.random() * 1_000_000_000);
    this.selectAllId = `${idPrefix}-all`;
    this.rows = params.conflicts.map((conflict, index) => ({
      conflict,
      existingRecord: describeExistingRecord(conflict),
      selected: ko.observable(false),
      action: ko.observable<ManualResolutionAction>(conflict.suggested_action),
      checkboxId: `${idPrefix}-${index}`,
    }));
    this.showSheet = this.rows.some((row) => row.conflict.sheet !== undefined);
    this.newCount = params.newCount;
    this.result = params.result;
    this.title = i18n.t('Resolve conflicts — {{count}} found', {
      count: params.conflicts.length,
    })();
    this.summary = i18n.t('{{count}} new records will be added.', { count: params.newCount })();
    this.allSelected = ko.pureComputed<boolean>({
      read: () => this.rows.length > 0 && this.rows.every((row) => row.selected()),
      write: (value: boolean) => this.rows.forEach((row) => row.selected(value)),
    });
  }

  applyToSelected = () => {
    const action = this.bulkAction();
    this.rows.filter((row) => row.selected()).forEach((row) => row.action(action));
  };

  importRecords = () => {
    const resolutions: RowResolution[] = this.rows.map((row) => ({
      sheet: row.conflict.sheet,
      row: row.conflict.row,
      action: row.action(),
    }));
    this.result.resolve(resolutions);
  };

  cancel = () => {
    this.result.reject();
  };

  dispose() { }
}

// A row can repeat an earlier row of the same sheet, which is not saved yet; say which row it is.
function describeExistingRecord(conflict: RowConflict): string {
  if (conflict.existing_row === null) {
    return conflict.existing_name;
  }
  return i18n.t('{{name}} (row {{row}} of this sheet)', {
    name: conflict.existing_name,
    row: conflict.existing_row,
    // bound as text, so a "/" or "&" in the name must not come out as an HTML entity
    interpolation: { escapeValue: false },
  })();
}

export const resolveImportConflicts = {
  name: 'resolve-import-conflicts',
  viewModel: ResolveImportConflicts,
  template,
};

ko.components.register(resolveImportConflicts.name, resolveImportConflicts);

// The "Action to take for duplicate records" choices shared by every Excel import that supports
// manual conflict resolution. Labels stay literal so the translation collector can pick them up.
export function buildDuplicateActionOptions(): {
  id: ImportDimensionsDuplicatesAction;
  name: string;
}[] {
  return [
    { id: 'manual', name: i18n.t('Manual conflict resolution')() },
    { id: 'insert', name: i18n.t('Add all records')() },
    {
      id: 'update_duplicates',
      name: i18n.t('Update records with an existing name (overwrites existing data)')(),
    },
    {
      id: 'ignore_duplicates',
      name: i18n.t('Ignore records with an existing name')(),
    },
    {
      id: 'update_duplicates_code',
      name: i18n.t('Update records with an existing code (overwrites existing data)')(),
    },
    {
      id: 'ignore_duplicates_code',
      name: i18n.t('Ignore records with an existing code')(),
    },
  ];
}

export interface ManualResolutionEndpoints {
  validate: (fileContents: ArrayBuffer) => Promise<ValidateImportResult>;
  commit: (fileContents: ArrayBuffer, resolutions: RowResolution[]) => Promise<CommitImportResult>;
}

// Dry-run the upload, let the user resolve each conflicting row in the dialog, then commit with
// those decisions. Resolves to null when the user cancels the dialog.
export async function runManualResolutionFlow(
  fileContents: ArrayBuffer,
  endpoints: ManualResolutionEndpoints
): Promise<ManualResolutionOutcome<string[]> | null> {
  const validation = await endpoints.validate(fileContents);
  if (validation.errors.length > 0) {
    return { data: [], errors: validation.errors, added_count: 0, updated_count: 0 };
  }

  let resolutions: RowResolution[] = [];
  if (validation.conflicts.length > 0) {
    try {
      resolutions = await app.formsStackController.push<RowResolution[], Params>({
        title: i18n.t('Resolve conflicts')(),
        name: resolveImportConflicts.name,
        isBig: true,
        params: {
          conflicts: validation.conflicts,
          newCount: validation.new_count,
          result: new Deferred<RowResolution[]>(),
        },
      });
    } catch {
      // The only rejection this promise can carry is the dialog's own cancel: the deferred it
      // waits on is settled by the component, and FormsStackController.push discards the reason.
      return null;
    }
  }

  return endpoints.commit(fileContents, resolutions);
}
